"use client"
import {
  AlertCircle,
  HelpCircle,
  Minus,
  Plus
} from 'lucide-react';
import Link from "next/link";
import { useEffect, useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQ {
  id: string;
  questions: FAQItem[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface FAQDisplayProps {
  blogSlug: string;
  title?: string;
  className?: string;
}

export default function FAQDisplay({ blogSlug, title = "Frequently Asked Questions", className = "" }: FAQDisplayProps) {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      if (!blogSlug) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/faqs/blog/${blogSlug}`);
        if (!response.ok) {
          throw new Error('Failed to fetch FAQs');
        }

        const data = await response.json();
        if (data.success) {
          setFaqs(data.faqs);
        } else {
          throw new Error(data.error || 'Failed to fetch FAQs');
        }
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch FAQs');
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, [blogSlug]);

  const toggleFAQ = (faqId: string) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm   ${className}`}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-gray-200 rounded-full animate-spin border-t-blue-600"></div>
              <HelpCircle className="absolute inset-0 m-auto h-5 w-5 text-blue-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Loading FAQs</h3>
              <p className="text-sm text-gray-500">Please wait while we fetch the questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm   ${className}`}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-red-900">Unable to Load FAQs</h3>
              <p className="text-sm text-red-600 max-w-md">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (faqs.length === 0) {
    return null; // Don't render anything if no FAQs
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm   ${className}`} data-interlink-allow="faq">
      <div className="p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8">
          <div className="flex items-center mb-4 sm:mb-0">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4">
              <HelpCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {title}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Find answers to common questions
              </p>
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
            <span className="font-medium">
              {faqs.reduce((total, faq) => total + faq.questions.length, 0)} Question{faqs.reduce((total, faq) => total + faq.questions.length, 0) !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq) => (
            faq.questions.map((qa) => {
              const faqId = `${faq.id}-${qa.id}`;
              const isExpanded = expandedFAQ === faqId;

              return (
                <div
                  key={faqId}
                  className="group border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-sm hover:border-gray-300"
                >
                  {/* Question Button */}
                  <button
                    onClick={() => toggleFAQ(faqId)}
                    className={`
                      w-full px-6 py-5 text-left
                      flex items-center justify-between
                      transition-all duration-200 ease-in-out rounded-lg
                      focus:outline-none  focus:ring-inset
                      hover:bg-gray-50
                      ${isExpanded ? 'bg-blue-50/50' : 'bg-white'}
                    `}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 flex-1">
                      <div className="flex-1">
                        <h3 className={`
                          text-base font-semibold text-gray-900 leading-tight
                          transition-colors duration-200
                          ${isExpanded ? 'text-blue-900' : 'text-gray-900'}
                        `}>
                          {qa.question}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-3">

                        <div className={`
                          w-8 h-8 rounded-lg flex items-center justify-center
                          transition-all duration-200 ease-in-out
                          ${isExpanded
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                          }
                        `}>
                          {isExpanded ? (
                            <Minus className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Answer Content */}
                  <div className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                  `}>
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-100 pt-6">
                        <div className="faq-answer-content prose prose-gray prose-sm max-w-none
                          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                          prose-strong:font-semibold prose-strong:text-gray-900
                          prose-a:text-blue-600 prose-a:hover:text-blue-800 prose-a:underline">
                          <div
                            className="text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: qa.answer }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
            <HelpCircle className="h-4 w-4" />
            <span>
              Can&apos;t find what you&apos;re looking for?
              <Link href="/contact-us" className="text-blue-600 hover:text-blue-800 font-medium ml-1">
                Contact us
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
